import { useMemo, useState } from 'react'
import { Eye } from 'lucide-react'
import { Button, Card, MoneyValue, Skeleton, StatusBadge } from '@shared/design-system/ui'
import { formatMoney } from '@shared/lib/format/currency'
import { useAdminProducts, useAdminSalesInfinite } from '../api/admin.api'
import { adminSaleStatusPresentation, saleFinancialImpact } from '../lib/admin-sale-events'
import { AdminSaleDetailDrawer } from './AdminSaleDetailDrawer'
import {
  amount,
  buyer,
  description,
  empty,
  header,
  identity,
  item,
  list,
  meta,
  root,
  title,
} from './AdminUserSalesPanel.css'

export function AdminUserSalesPanel({ userId }: { userId: string }) {
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null)
  const sales = useAdminSalesInfinite({ participantId: userId })
  const products = useAdminProducts(userId)
  const items = sales.data?.pages.flatMap((page) => page.items) ?? []
  const productNames = useMemo(
    () => new Map((products.data?.items ?? []).map((product) => [product.id, product.name])),
    [products.data?.items],
  )

  return (
    <div className={root} role="tabpanel">
      <div className={header}>
        <h2 className={title}>Vendas do usuário</h2>
        <p className={description}>
          Histórico operacional mais recente. Abra uma venda para conferir pagamento, participantes
          e lançamentos financeiros.
        </p>
      </div>

      <Card padding="md">
        {sales.isLoading ? (
          <Skeleton height={220} />
        ) : sales.isError ? (
          <div className={empty}>Não foi possível carregar as vendas deste usuário.</div>
        ) : items.length === 0 ? (
          <div className={empty}>Este usuário ainda não participa de nenhuma venda.</div>
        ) : (
          <div className={list}>
            {items.map((sale) => {
              const status = adminSaleStatusPresentation(sale)
              const impact = saleFinancialImpact(sale)
              return (
                <div className={item} key={sale.id}>
                  <div className={identity}>
                    <span className={buyer}>
                      {sale.buyerName || sale.buyerEmail || 'Comprador'}
                    </span>
                    <span className={meta}>
                      {productNames.get(sale.productId) ?? `Produto ${sale.productId}`} ·{' '}
                      {formatDateTime(sale.createdAt)}
                    </span>
                    <span className={meta}>Venda {sale.id}</span>
                    {sale.coupon ? <span className={meta}>Cupom aplicado</span> : null}
                  </div>
                  <StatusBadge label={status.label} tone={status.tone} />
                  <span className={amount}>
                    <MoneyValue amount={impact.active.amount} currency={impact.active.currency} />
                    {impact.hasImpact ? (
                      <>
                        <span className={meta}>
                          de {formatMoney(impact.original.amount, impact.original.currency)}{' '}
                          continuam válidos
                        </span>
                        <span className={meta}>
                          {formatMoney(impact.affected.amount, impact.affected.currency)} foram
                          reembolsados ou contestados
                        </span>
                      </>
                    ) : null}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Eye size={15} />}
                    onClick={() => setSelectedSaleId(sale.id)}
                  >
                    Ver venda
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {sales.hasNextPage ? (
        <Button
          variant="secondary"
          disabled={sales.isFetchingNextPage}
          onClick={() => void sales.fetchNextPage()}
        >
          {sales.isFetchingNextPage ? 'Carregando...' : 'Carregar mais vendas'}
        </Button>
      ) : null}

      <AdminSaleDetailDrawer saleId={selectedSaleId} onClose={() => setSelectedSaleId(null)} />
    </div>
  )
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}
